import React from 'react';
import { useLocation } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';

import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import { PageProvider } from '../contexts/PageContext.jsx';
import PermissionsContext from '../contexts/PermissionsContext.jsx';
import { SearchProvider } from '../contexts/SearchContext.jsx';
import SideBar from './SideBar.jsx';
import TopBar from './TopBar.jsx';

const DRAWER_WIDTH = 200;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open, mobile }) => ({
    padding: theme.spacing(2),
    paddingTop: 1,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    width: '100%',
    ...(open &&
      !mobile && {
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: `${DRAWER_WIDTH}px`,
        width: `calc(100% - ${DRAWER_WIDTH}px)`,
      }),
  })
);

function Layout({ children }) {
  const mobile = useMediaQuery('(max-width:899px)') ? 1 : 0;
  const { permissions } = React.useContext(PermissionsContext);
  const hasPermissions = permissions && permissions.length > 0;

  const [open, setOpen] = React.useState(true);

  const handleClick = React.useCallback(() => setOpen((prev) => !prev), []);

  // added to populate tab title based on route
  const location = useLocation();

  React.useEffect(() => {
    const path = location.pathname || '';

    // Put your real routes here (prefix match is usually best)
    const titleByPrefix = [
      ['/ecg-viewer', 'ECG-VIEWER'],
      ['/hr-report', 'HR Report'],
      ['/order-entry', 'Order Entry'],
      ['/customer-summary', 'Customer Summary'],
      ['/commissions', 'Commissions'],
      ['/commgross', 'Gross Commissions'],
      ['/daily-summary', 'Daily Summary'],
      ['/performance', 'Territory Peformance'],
      ['/outstanding-orders', 'Outstanding Orders'],
      ['/trackinginfo', 'Shipping Tracking'],
      ['/bank-posting', 'Bank Posting'],
      ['/order-requirements', 'TZMMS Order Fulfillment'],
      ['/tzmms-activity', 'TZMMS Activity'],
      ['/products-on-back-order', 'Products On Back Order'],
      ['/TZMMS-INV', 'TZMMS Inventory Counting'],
    ];

    const matched = titleByPrefix.find(([prefix]) => path.startsWith(prefix));
    const pageTitle = matched ? matched[1] : 'TZ App';
    // if we don't like - TZ we can remove here
    document.title = `${pageTitle} — TZ`;
  }, [location.pathname]);

  const handleCloseMobile = React.useCallback(() => {
    if (mobile) {
      setOpen(false);
    }
  }, [mobile]);

  return (
    <SnackbarProvider maxSnack={4}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <SearchProvider>
          <PageProvider>
            <CssBaseline />
            <TopBar
              onClick={hasPermissions ? handleClick : undefined}
              onClose={handleCloseMobile}
            />
            {hasPermissions && (
              <Drawer
                sx={{
                  width: DRAWER_WIDTH,
                  flexShrink: 0,
                  '& .MuiDrawer-paper': {
                    width: DRAWER_WIDTH,
                    boxSizing: 'border-box',
                  },
                }}
                variant={mobile ? 'temporary' : 'persistent'}
                ModalProps={{
                  keepMounted: true,
                }}
                anchor="left"
                open={open}
                onClose={handleCloseMobile}
              >
                <Toolbar />
                <SideBar onClick={handleCloseMobile} />
              </Drawer>
            )}
            <Toolbar />
            <Main open={open && hasPermissions} mobile={mobile}>
              {children}
            </Main>
          </PageProvider>
        </SearchProvider>
      </LocalizationProvider>
    </SnackbarProvider>
  );
}

export default Layout;
